import React, { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Sparkles, Download, Plus, ExternalLink, Copy, MapPin, Link2, Braces, Loader2, CheckCircle2,
} from "lucide-react";
import {
  C, ScoreRing, StatCard, Panel, SeverityChip, PriorityBadge, LabelPill, NotConnectedNote, Empty, downloadCsv,
} from "./seoUi";

const Th = ({ children, className = "" }) => (
  <th className={`text-left font-semibold px-3 py-2 text-[12px] uppercase tracking-wide ${className}`} style={{ color: C.slate }}>{children}</th>
);
const Td = ({ children, className = "" }) => (
  <td className={`px-3 py-2 text-[13px] align-top ${className}`} style={{ color: C.charcoal }}>{children}</td>
);
const TableWrap = ({ children }) => (
  <div className="overflow-x-auto rounded-lg border" style={{ borderColor: C.line }}>
    <table className="w-full border-collapse">{children}</table>
  </div>
);
const Chips = ({ items, tone = C.navy }) => (
  <div className="flex flex-wrap gap-1">
    {(items || []).map((t, i) => (
      <span key={i} className="text-[11px] font-medium px-1.5 py-0.5 rounded" style={{ backgroundColor: `${tone}12`, color: tone }}>{t}</span>
    ))}
  </div>
);

const TaskBtn = ({ onClick }) => (
  <Button size="sm" variant="outline" onClick={onClick} data-testid="create-task-btn"
    className="h-8 gap-1 border-[#D6E0E7] text-[12px]" style={{ color: C.navy }}>
    <Plus size={13} /> Task
  </Button>
);

// ============================ OVERVIEW ============================
export const OverviewTab = ({ report, aiSummary, aiLoading, onGenerateAi, onCreateTask, scopeLabel }) => {
  const h = report.health || {};
  const is = report.issue_summary || {};
  const recs = report.recommendations || [];
  const dist = h.by_label || {};
  return (
    <div className="space-y-5" data-testid="seo-tab-overview">
      <div className="grid gap-4 lg:grid-cols-[auto,1fr]">
        <Panel testid="overview-health-panel">
          <div className="flex items-center gap-5">
            <ScoreRing score={h.score || 0} label={h.label} testid="overview-score-ring" />
            <div>
              <div className="text-[12px] font-semibold uppercase tracking-wide" style={{ color: C.slate }}>Overall SEO Health</div>
              <div className="font-display font-bold text-xl" style={{ color: C.navy }}>{h.label}</div>
              <div className="text-[12.5px] mt-1" style={{ color: C.slate }}>{scopeLabel}</div>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {Object.entries(dist).map(([k, v]) => (
                  <span key={k} className="text-[11px] font-medium px-2 py-0.5 rounded-full" style={{ backgroundColor: `${C.navy}0F`, color: C.navy }}>{k}: {v}</span>
                ))}
              </div>
            </div>
          </div>
          <div className="text-[11.5px] mt-3" style={{ color: C.slate }}>{h.note}</div>
        </Panel>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <StatCard testid="stat-pages" label="Pages audited" value={h.pages_total || 0} />
          <StatCard testid="stat-high" label="High issues" value={is.high || 0} tone={C.red} />
          <StatCard testid="stat-medium" label="Medium issues" value={is.medium || 0} tone={C.amber} />
          <StatCard testid="stat-low" label="Low issues" value={is.low || 0} tone={C.navy} />
          <StatCard testid="stat-sitemap" label="Sitemap-ready" value={report.sitemap?.total || 0} tone={C.green} />
          <StatCard testid="stat-local" label="Local territories" value={(report.local || []).length} tone={C.green} />
        </div>
      </div>

      <Panel title="AI Summary (optional)" testid="overview-ai-panel"
        subtitle="Generated from internal, public SEO readiness findings only — no client, lead, or private data."
        right={<Button data-testid="ai-summary-btn" disabled={aiLoading} onClick={onGenerateAi} className="gap-1.5 bg-[#1C4358] hover:bg-[#16374a] text-white">
          {aiLoading ? <Loader2 className="animate-spin" size={15} /> : <Sparkles size={15} />} {aiSummary ? "Regenerate" : "Generate AI Summary"}
        </Button>}>
        {aiSummary ? (
          <div data-testid="ai-summary-text" className="text-[14px] whitespace-pre-wrap leading-relaxed" style={{ color: C.charcoal }}>
            {aiSummary.summary}
            <div className="text-[11.5px] mt-3 italic" style={{ color: C.slate }}>{aiSummary.disclaimer}</div>
          </div>
        ) : (
          <p className="text-[13px]" style={{ color: C.slate }}>
            Rules-based recommendations below are always the source of truth. Click “Generate AI Summary” for a short, plain-language coaching note.
          </p>
        )}
      </Panel>

      <Panel title="Prioritized Recommendations" subtitle="Rules-based — the source of truth." testid="overview-recs-panel">
        {recs.length ? (
          <div className="space-y-2">
            {recs.map((r, i) => (
              <div key={i} data-testid={`rec-row-${i}`} className="flex items-start justify-between gap-3 rounded-lg border p-3" style={{ borderColor: C.line }}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <PriorityBadge priority={r.priority} />
                    <span className="font-semibold text-[14px]" style={{ color: C.navy }}>{r.title}</span>
                  </div>
                  <div className="text-[13px] mt-0.5" style={{ color: C.slate }}>{r.detail}</div>
                  {r.url && <div className="text-[12px] mt-0.5 font-mono" style={{ color: C.sky }}>{r.url}</div>}
                </div>
                <TaskBtn onClick={() => onCreateTask({ page_url: r.url, issue_summary: r.title, recommended_fix: r.detail, priority: r.priority, franchise_id: r.franchise_id, service_lane: r.service_lane })} />
              </div>
            ))}
          </div>
        ) : <Empty>No recommendations — everything looks healthy.</Empty>}
      </Panel>
    </div>
  );
};

// ============================ PAGE AUDIT ============================
export const PageAuditTab = ({ report, onCreateTask }) => {
  const [q, setQ] = useState("");
  const pages = (report.pages || []).filter((p) =>
    !q || p.url.toLowerCase().includes(q.toLowerCase()) || (p.title || "").toLowerCase().includes(q.toLowerCase()));
  const exportCsv = () => downloadCsv("seo-page-audit.csv", pages.map((p) => ({
    url: p.url, page_type: p.page_type, territory: p.territory, service_lane: p.service_lane,
    score: p.seo_score, status: p.seo_label, title: p.title, title_len: p.title_len,
    meta_len: p.meta_len, h1: p.h1, schema: (p.schema || []).join("|"), internal_links: p.internal_links,
    in_sitemap: p.in_sitemap, content_depth: p.content_depth, next_action: p.next_action,
  })));
  return (
    <Panel title="Page Audit" subtitle={`${pages.length} pages · native readiness scoring`} testid="seo-tab-pages"
      right={<div className="flex items-center gap-2">
        <Input data-testid="page-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter by URL / title" className="h-9 w-48 text-[13px]" />
        <Button data-testid="pages-export-btn" size="sm" variant="outline" onClick={exportCsv} className="h-9 gap-1 border-[#D6E0E7]" style={{ color: C.navy }}><Download size={14} /> CSV</Button>
      </div>}>
      {pages.length ? (
        <TableWrap>
          <thead><tr style={{ background: "#F4F8FB" }}>
            <Th>Page</Th><Th>Type</Th><Th>Title</Th><Th>Meta</Th><Th className="text-center">Schema</Th><Th className="text-center">Links</Th><Th className="text-center">Score</Th><Th>Status</Th><Th>Next action</Th><Th></Th>
          </tr></thead>
          <tbody>
            {pages.map((p, i) => (
              <tr key={i} data-testid={`page-row-${i}`} className="border-t" style={{ borderColor: C.line }}>
                <Td><div className="font-mono text-[12.5px]" style={{ color: C.navy }}>{p.url}</div>{p.territory && <div className="text-[11px]" style={{ color: C.slate }}>{p.territory}</div>}</Td>
                <Td><span className="text-[12px]">{p.page_type}</span></Td>
                <Td><span className={p.title ? "" : "italic"} style={{ color: p.title ? C.charcoal : C.red }}>{p.title || "missing"}</span><div className="text-[11px]" style={{ color: (p.title_len >= 30 && p.title_len <= 65) ? C.green : C.amber }}>{p.title_len} ch</div></Td>
                <Td><div className="text-[11px]" style={{ color: (p.meta_len >= 70 && p.meta_len <= 165) ? C.green : C.amber }}>{p.meta_description ? `${p.meta_len} ch` : <span style={{ color: C.red }}>missing</span>}</div></Td>
                <Td className="text-center">{(p.schema || []).length || <span style={{ color: C.red }}>0</span>}</Td>
                <Td className="text-center"><span style={{ color: p.internal_links >= 3 ? C.charcoal : C.amber }}>{p.internal_links}</span></Td>
                <Td className="text-center"><span className="font-display font-bold" style={{ color: p.seo_label === "Not Enough Data" ? C.slate : p.seo_score >= 85 ? C.green : p.seo_score >= 60 ? C.amber : C.red }}>{p.seo_score}</span></Td>
                <Td><LabelPill label={p.seo_label} score={p.seo_score} /></Td>
                <Td><span className="text-[12.5px]">{p.next_action}</span></Td>
                <Td><TaskBtn onClick={() => onCreateTask({ page_url: p.url, page_type: p.page_type, issue_summary: p.next_action, recommended_fix: p.next_action, priority: (p.issues?.[0]?.severity) || "medium", franchise_id: p.franchise_id, service_lane: p.service_lane, territory_label: p.territory })} /></Td>
              </tr>
            ))}
          </tbody>
        </TableWrap>
      ) : <Empty />}
    </Panel>
  );
};

// ============================ TECHNICAL ISSUES ============================
export const IssuesTab = ({ report }) => {
  const issues = report.technical_issues || [];
  const is = report.issue_summary || {};
  const exportCsv = () => downloadCsv("seo-technical-issues.csv", issues);
  return (
    <div className="space-y-4" data-testid="seo-tab-issues">
      <div className="grid grid-cols-3 gap-3">
        <StatCard testid="issue-high" label="High" value={is.high || 0} tone={C.red} />
        <StatCard testid="issue-medium" label="Medium" value={is.medium || 0} tone={C.amber} />
        <StatCard testid="issue-low" label="Low" value={is.low || 0} tone={C.navy} />
      </div>
      <Panel title="Technical Issues" subtitle="Highest severity first" testid="issues-panel"
        right={<Button data-testid="issues-export-btn" size="sm" variant="outline" onClick={exportCsv} className="h-9 gap-1 border-[#D6E0E7]" style={{ color: C.navy }}><Download size={14} /> CSV</Button>}>
        {issues.length ? (
          <TableWrap>
            <thead><tr style={{ background: "#F4F8FB" }}><Th>Severity</Th><Th>Issue</Th><Th>Page</Th><Th>Type</Th><Th>Territory</Th></tr></thead>
            <tbody>
              {issues.map((it, i) => (
                <tr key={i} data-testid={`issue-row-${i}`} className="border-t" style={{ borderColor: C.line }}>
                  <Td><SeverityChip severity={it.severity} /></Td>
                  <Td><span className="text-[13px]">{it.message}</span><div className="text-[11px] font-mono" style={{ color: C.slate }}>{it.code}</div></Td>
                  <Td><span className="font-mono text-[12px]" style={{ color: C.navy }}>{it.url}</span></Td>
                  <Td>{it.page_type}</Td>
                  <Td>{it.territory || "—"}</Td>
                </tr>
              ))}
            </tbody>
          </TableWrap>
        ) : <Empty>No technical issues found.</Empty>}
      </Panel>
    </div>
  );
};

// ============================ LOCAL SEO ============================
const localItemLabel = {
  local_landing_page: "Landing page", local_service_lanes: "Service lanes", mapped_area: "Mapped area",
  local_team: "Local team", local_partners: "Local partners", local_testimonials: "Testimonials",
  local_faq: "FAQ", local_resources: "Resources", gbp_export_ready: "GBP export", local_schema: "Schema", sitemap: "Sitemap",
};
export const LocalSeoTab = ({ report }) => {
  const local = report.local || [];
  return (
    <div className="space-y-4" data-testid="seo-tab-local">
      {local.length ? local.map((sc) => (
        <Panel key={sc.franchise_id} testid={`local-card-${sc.franchise_id}`}>
          <div className="flex items-start gap-5 flex-wrap">
            <ScoreRing score={sc.score} label={sc.label} size={92} stroke={9} testid={`local-ring-${sc.franchise_id}`} />
            <div className="flex-1 min-w-[240px]">
              <div className="flex items-center gap-2 flex-wrap">
                <MapPin size={16} style={{ color: C.green }} />
                <span className="font-display font-semibold text-[16px]" style={{ color: C.navy }}>{sc.name}</span>
                <LabelPill label={sc.label} score={sc.score} />
                <span className="text-[11px] px-2 py-0.5 rounded-full capitalize" style={{ backgroundColor: `${C.navy}0F`, color: C.navy }}>{sc.status}</span>
              </div>
              {sc.local_path && <a href={sc.local_path} target="_blank" rel="noreferrer" className="text-[12.5px] font-mono inline-flex items-center gap-1 mt-1" style={{ color: C.sky }}>{sc.local_path} <ExternalLink size={11} /></a>}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 mt-3">
                {Object.entries(sc.items || {}).map(([k, v]) => (
                  <div key={k} className="flex items-center gap-1.5 text-[12.5px]">
                    <CheckCircle2 size={14} style={{ color: v === "ok" ? C.green : "#CBD5E1" }} />
                    <span style={{ color: v === "ok" ? C.charcoal : C.slate }}>{localItemLabel[k] || k}</span>
                  </div>
                ))}
              </div>
              <div className="text-[12.5px] mt-3 rounded-lg px-3 py-2" style={{ backgroundColor: "rgba(47,125,90,0.08)", color: C.green }}>Next: {sc.next_action}</div>
            </div>
          </div>
        </Panel>
      )) : <Empty>No local territories in scope yet.</Empty>}
    </div>
  );
};

// ============================ CONTENT GAPS ============================
export const GapsTab = ({ report, onCreateTask }) => {
  const gaps = report.content_gaps || [];
  const exportCsv = () => downloadCsv("seo-content-gaps.csv", gaps);
  return (
    <Panel title="Content Gaps" subtitle="Opportunities to strengthen pages" testid="seo-tab-gaps"
      right={<Button data-testid="gaps-export-btn" size="sm" variant="outline" onClick={exportCsv} className="h-9 gap-1 border-[#D6E0E7]" style={{ color: C.navy }}><Download size={14} /> CSV</Button>}>
      {gaps.length ? (
        <TableWrap>
          <thead><tr style={{ background: "#F4F8FB" }}><Th>Severity</Th><Th>Gap</Th><Th>Page</Th><Th>Recommended fix</Th><Th></Th></tr></thead>
          <tbody>
            {gaps.map((g, i) => (
              <tr key={i} data-testid={`gap-row-${i}`} className="border-t" style={{ borderColor: C.line }}>
                <Td><SeverityChip severity={g.severity} /></Td>
                <Td><span className="text-[13px]">{g.gap}</span></Td>
                <Td><span className="font-mono text-[12px]" style={{ color: C.navy }}>{g.url}</span>{g.territory && <div className="text-[11px]" style={{ color: C.slate }}>{g.territory}</div>}</Td>
                <Td><span className="text-[12.5px]" style={{ color: C.slate }}>{g.fix}</span></Td>
                <Td><TaskBtn onClick={() => onCreateTask({ page_url: g.url, page_type: g.page_type, issue_summary: g.gap, recommended_fix: g.fix, priority: g.severity, territory_label: g.territory })} /></Td>
              </tr>
            ))}
          </tbody>
        </TableWrap>
      ) : <Empty>No content gaps detected.</Empty>}
    </Panel>
  );
};

// ============================ LINKS ============================
export const LinksTab = ({ report }) => {
  const orphans = report.links?.orphans || [];
  const suggestions = report.links?.suggestions || [];
  return (
    <div className="space-y-4" data-testid="seo-tab-links">
      <Panel title="Orphan / Low-link Pages" subtitle="Pages with fewer than 3 internal links" testid="links-orphans-panel">
        {orphans.length ? (
          <TableWrap>
            <thead><tr style={{ background: "#F4F8FB" }}><Th>Page</Th><Th>Type</Th><Th className="text-center">Internal links</Th></tr></thead>
            <tbody>
              {orphans.map((o, i) => (
                <tr key={i} className="border-t" style={{ borderColor: C.line }}>
                  <Td><span className="font-mono text-[12.5px]" style={{ color: C.navy }}>{o.url}</span></Td>
                  <Td>{o.page_type}</Td><Td className="text-center"><span style={{ color: C.amber }}>{o.internal_links}</span></Td>
                </tr>
              ))}
            </tbody>
          </TableWrap>
        ) : <Empty>No orphan pages — good internal linking.</Empty>}
      </Panel>
      <Panel title="Internal Linking Suggestions" testid="links-suggestions-panel">
        {suggestions.length ? (
          <div className="space-y-2">
            {suggestions.map((s, i) => (
              <div key={i} className="flex items-start gap-2 rounded-lg border p-3" style={{ borderColor: C.line }}>
                <Link2 size={15} style={{ color: C.sky }} className="mt-0.5 shrink-0" />
                <div>
                  <div className="text-[13px]"><span className="font-mono" style={{ color: C.navy }}>{s.from}</span> → <span className="font-mono" style={{ color: C.green }}>{s.to}</span></div>
                  <div className="text-[12.5px]" style={{ color: C.slate }}>{s.reason}</div>
                </div>
              </div>
            ))}
          </div>
        ) : <Empty>No linking suggestions.</Empty>}
      </Panel>
    </div>
  );
};

// ============================ SCHEMA ============================
export const SchemaTab = ({ report }) => {
  const rows = report.schema || [];
  const [open, setOpen] = useState(false);
  const [jsonld, setJsonld] = useState("");
  const [loading, setLoading] = useState(false);
  const [title, setTitle] = useState("");

  const view = async (row) => {
    setTitle(row.url); setOpen(true); setLoading(true); setJsonld("");
    try {
      const { data } = await api.get(row.json_source);
      const found = data?.json_ld || data?.jsonLd || data?.seo?.json_ld;
      if (found) setJsonld(JSON.stringify(found, null, 2));
      else setJsonld(`// JSON-LD not exposed via API for this page.\n// Expected schema types on this page:\n${JSON.stringify(row.types || [], null, 2)}`);
    } catch {
      setJsonld(`// Could not load JSON-LD.\n// Expected schema types:\n${JSON.stringify(row.types || [], null, 2)}`);
    } finally { setLoading(false); }
  };
  const copy = () => { navigator.clipboard?.writeText(jsonld); toast.success("JSON-LD copied to clipboard."); };

  return (
    <Panel title="Schema / Structured Data Monitor" subtitle="Detected schema types per page and what's missing" testid="seo-tab-schema">
      {rows.length ? (
        <TableWrap>
          <thead><tr style={{ background: "#F4F8FB" }}><Th>Page</Th><Th>Type</Th><Th>Detected schema</Th><Th>Missing</Th><Th></Th></tr></thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} data-testid={`schema-row-${i}`} className="border-t" style={{ borderColor: C.line }}>
                <Td><span className="font-mono text-[12.5px]" style={{ color: C.navy }}>{r.url}</span></Td>
                <Td>{r.page_type}</Td>
                <Td>{(r.types || []).length ? <Chips items={r.types} tone={C.green} /> : <span className="text-[12px]" style={{ color: C.red }}>none</span>}</Td>
                <Td>{(r.missing || []).length ? <Chips items={r.missing} tone={C.amber} /> : <span className="text-[12px]" style={{ color: C.green }}>—</span>}</Td>
                <Td>{r.json_source && <Button size="sm" variant="outline" data-testid={`schema-view-${i}`} onClick={() => view(r)} className="h-8 gap-1 border-[#D6E0E7] text-[12px]" style={{ color: C.navy }}><Braces size={13} /> JSON-LD</Button>}</Td>
              </tr>
            ))}
          </tbody>
        </TableWrap>
      ) : <Empty />}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="jsonld-dialog" aria-describedby={undefined}>
          <DialogHeader><DialogTitle className="text-[15px] font-mono">{title}</DialogTitle></DialogHeader>
          <div className="flex justify-end -mt-2"><Button size="sm" variant="outline" onClick={copy} disabled={loading} className="h-8 gap-1 border-[#D6E0E7]" style={{ color: C.navy }}><Copy size={13} /> Copy</Button></div>
          <pre className="text-[12px] rounded-lg p-3 overflow-auto max-h-[55vh]" style={{ background: "#0F2530", color: "#D9E6EE" }}>
            {loading ? "Loading…" : jsonld}
          </pre>
        </DialogContent>
      </Dialog>
    </Panel>
  );
};

// ============================ INTEGRATIONS ============================
export const IntegrationsTab = ({ report }) => {
  const items = report.integrations || [];
  return (
    <div className="space-y-4" data-testid="seo-tab-integrations">
      <NotConnectedNote>
        External SEO data sources are <b>not connected yet</b>. Until then, this dashboard uses internal data only — no rankings, traffic, search volume, or backlinks are estimated or fabricated.
      </NotConnectedNote>
      <div className="grid gap-3 sm:grid-cols-2">
        {items.map((it) => (
          <div key={it.key} data-testid={`integration-${it.key}`} className="rounded-[0.9rem] border bg-white p-4" style={{ borderColor: C.line }}>
            <div className="flex items-center justify-between gap-2">
              <span className="font-display font-semibold text-[15px]" style={{ color: C.navy }}>{it.name}</span>
              <span className="text-[11px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: it.connected ? `${C.green}1A` : "rgba(75,85,99,0.12)", color: it.connected ? C.green : C.slate }}>
                {it.connected ? "Connected" : "Not connected yet"}
              </span>
            </div>
            <p className="text-[13px] mt-2" style={{ color: C.charcoal }}>{it.provides}</p>
            <p className="text-[12px] mt-1.5" style={{ color: C.slate }}>Until connected: {it.manual_until_connected}</p>
          </div>
        ))}
      </div>
    </div>
  );
};
